import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import noimg from "../../icons/noimg.png";

export default function ZoomBox({
  src,
  zoom = 2.5,
  lensSize = 200,
  border = "2px solid #fff",
  shadow = "0 4px 16px rgba(0,0,0,0.35)",
  style = {},
  allowOverflow = true,
  hideOutsideImage = true,
  zoomMode,
}) {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const lensRef = useRef(null);

  const metricsRef = useRef(null); // {drawW,drawH,offX,offY,padL,padT,r,bgW,bgH,originX,originY}
  const rafRef = useRef(0);
  const shownRef = useRef(false);

  const computeMetrics = () => {
    const wrap = wrapRef.current;
    const img = imgRef.current;
    const lens = lensRef.current;
    if (!wrap || !img || !lens) return;

    const rect = wrap.getBoundingClientRect();
    const cs = getComputedStyle(wrap);
    const padL = parseFloat(cs.paddingLeft) || 0;
    const padR = parseFloat(cs.paddingRight) || 0;
    const padT = parseFloat(cs.paddingTop) || 0;
    const padB = parseFloat(cs.paddingBottom) || 0;

    // content-box 크기
    const cw = rect.width - padL - padR;
    const ch = rect.height - padT - padB;

    const ir = img.naturalWidth / img.naturalHeight; // 이미지 비율
    const wr = cw / ch;

    // object-fit: contain 으로 실제 그려진 영역(콘텐츠 박스 내부 기준)
    let drawW, drawH, offX, offY;
    if (wr > ir) {
      drawH = ch;
      drawW = ch * ir;
      offX = (cw - drawW) / 2;
      offY = 0;
    } else {
      drawW = cw;
      drawH = cw / ir;
      offX = 0;
      offY = (ch - drawH) / 2;
    }

    const r = lensSize / 2;
    const bgW = drawW * zoom;
    const bgH = drawH * zoom;

    metricsRef.current = {
      drawW,
      drawH,
      offX,
      offY,
      padL,
      padT,
      r,
      bgW,
      bgH,
      originX: rect.left + padL, // viewport 기준 content-box 원점
      originY: rect.top + padT,
    };

    // 렌즈 고정 스타일
    lens.style.width = `${lensSize}px`;
    lens.style.height = `${lensSize}px`;
    lens.style.left = "0";
    lens.style.top = "0";
    lens.style.backgroundImage = `url(${src})`;
    lens.style.backgroundRepeat = "no-repeat";
    lens.style.backgroundSize = `${bgW}px ${bgH}px`;
  };

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    if (img.complete) computeMetrics();
    else img.addEventListener("load", computeMetrics, { once: true });

    const ro = new ResizeObserver(computeMetrics);
    if (wrapRef.current) ro.observe(wrapRef.current);

    return () => ro.disconnect();
  }, [src, zoom, lensSize, zoomMode]);

  const updateLens = (clientX, clientY) => {
    const wrap = wrapRef.current;
    const lens = lensRef.current;
    const m = metricsRef.current;
    if (!wrap || !lens || !m) return;

    const {
      originX,
      originY,
      drawW,
      drawH,
      offX,
      offY,
      padL,
      padT,
      r,
      bgW,
      bgH,
    } = m;

    // content-box 좌표 (패딩 보정 된 기준)
    const x = clientX - originX;
    const y = clientY - originY;

    // 표시 제어: 박스 위에 있으면 보이기, 벗어나면 숨기기 (원하면 항상 보이게 바꿔도 됨)
    const rect = wrap.getBoundingClientRect();
    const insideWrapper =
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom;

    if (!insideWrapper) {
      if (shownRef.current) {
        lens.style.display = "none";
        shownRef.current = false;
      }
      return;
    }
    const insideImage =
      x >= offX && x <= offX + drawW && y >= offY && y <= offY + drawH;
    if (hideOutsideImage && !insideImage) {
      if (shownRef.current) {
        lens.style.display = "none";
        shownRef.current = false;
      }
      return;
    }
    if (!shownRef.current) {
      lens.style.display = "block";
      shownRef.current = true;
    }

    // 🔑 렌즈 위치는 클램프하지 않음 → 박스/이미지 밖으로도 자유롭게
    lens.style.transform = `translate3d(${padL + x - r}px, ${
      padT + y - r
    }px, 0)`;

    // 배경 위치는 이미지 그려진 영역을 기준으로만 클램프
    // (렌즈가 영역 밖일 때도 가장자리 픽셀을 보여주기 위함)
    const clampedX = Math.max(0, Math.min(x - offX, drawW));
    const clampedY = Math.max(0, Math.min(y - offY, drawH));
    const normX = clampedX / drawW; // 0~1
    const normY = clampedY / drawH; // 0~1
    const bgX = -(normX * bgW - r);
    const bgY = -(normY * bgH - r);
    lens.style.backgroundPosition = `${bgX}px ${bgY}px`;
  };

  const onPointerMove = (e) => {
    if (rafRef.current) return;
    const { clientX, clientY } = e;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      updateLens(clientX, clientY);
    });
  };

  const onLeave = () => {
    const lens = lensRef.current;
    if (lens) lens.style.display = "none";
    shownRef.current = false;
  };

  return (
    <Box
      ref={wrapRef}
      onPointerMove={onPointerMove}
      onPointerLeave={onLeave}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: allowOverflow ? "visible" : "hidden",
        ...style,
      }}
    >
      <img
        ref={imgRef}
        src={src}
        alt="zoomable"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
          userSelect: "none",
          pointerEvents: "none",
          filter:
            localStorage.getItem("demoMode") === "true" ? "blur(3px)" : "none",
          WebkitFilter:
            localStorage.getItem("demoMode") === "true" ? "blur(3px)" : "none",
        }}
        onError={(e) => {
          e.target.src = noimg;
        }}
      />

      {zoomMode && (
        <div
          ref={lensRef}
          style={{
            position: "absolute",
            display: "none",
            border,
            boxShadow: shadow,
            borderRadius: "50%",
            pointerEvents: "none",
            willChange: "transform, background-position",
            transform: "translate3d(0,0,0)",
          }}
        />
      )}
    </Box>
  );
}
